import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, FirstItem, LastTableItem, Details, TableBodyRow } from './ProductsPage.styles.js';

import { formatDate } from '../../utils/utils.js';
import openToast from '../../utils/toast.js';

import MainContainer from '../../components/utils/MainContainer/MainContainer.js';
import Table from '../../components/Table/Table.js';
import TableItem from '../../components/Table/TableItem.js';

import { selectFetchPartnersItemsPending, selectPartnersItems } from '../../store/slices/partners/slice.js';
import { fetchPartnersItems } from '../../store/slices/partners/asyncThunks.js';

const tableHeader = [
  {
    label: 'product-name',
    sortable: true,
    key: 'productName',
  },
  {
    label: 'created-at',
    sortable: true,
    key: 'createdAt',
  },
  {
    label: 'partners-name',
    sortable: true,
    key: 'partnersName',
  },
  {
    label: 'status',
    sortable: true,
    key: 'status',
  },
  {
    label: 'details',
    sortable: true,
    key: 'details',
  },
  {
    label: 'actions',
    sortable: false,
    key: 'actions',
  },
];

const ProductsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastId = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { data, maxReached, page, total, perPage } = useSelector(selectPartnersItems);
  const fetchPartnersItemsPending = useSelector(selectFetchPartnersItemsPending);

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const pageParam = queryParams.get('page');

  useEffect(() => {
    dispatch(fetchPartnersItems({ page: pageParam }))
      .unwrap()
      .then(() => {})
      .catch(err => openToast(toastId, 'error-fetching', 'error', err.message));
  }, [dispatch, pageParam]);

  const handlePageChange = pageNumber => {
    const query = { page: pageNumber };
    const queryString = new URLSearchParams(query).toString();
    const url = `/products?${queryString}`;

    navigate(url);
  };

  return (
    <MainContainer pageTitle={'products'}>
      <Table
        key={searchTerm}
        headerInformation={tableHeader}
        totalCount={total}
        currentPage={+pageParam}
        perPage={perPage}
        noDataContent={t('no-data')}
        maxReached={maxReached}
        dataLength={data.length}
        setPage={handlePageChange}
        lastColumnAligned
        dataLoading={fetchPartnersItemsPending}
        centerText>
        {data.map((item, key) => {
          return (
            <TableBodyRow key={key} $offClick>
              <FirstItem showHover data={item?.name} />
              <TableItem data={formatDate(item?.createdAt) || '/'} center />
              <TableItem data={item?.partnerUser?.fullName || '/'} center />
              <TableItem data={item?.approved ? t('active') : t('pending')} center />
              <TableItem
                data={
                  <Details
                    onClick={() => {
                      navigate(`/product-details/${item.id}`);
                    }}
                  />
                }
                center
              />
              <LastTableItem
                $offClick
                data={
                  !item?.approved ? (
                    <Button
                      onClick={() => {
                        navigate(`/approve-product/${item.id}`);
                      }}>
                      {t('approve')}
                    </Button>
                  ) : (
                    <Button
                      $accepted
                      onClick={() => {
                        navigate(`/product-details/${item.id}`, {
                          state: { isEditing: true },
                        });
                      }}>
                      {t('edit')}
                    </Button>
                  )
                }
              />
            </TableBodyRow>
          );
        })}
      </Table>
    </MainContainer>
  );
};

export default ProductsPage;
